/**
 * NumberStepper — числовое поле со степпером «− N +» (владелец 2026-08-14:
 * числовой тип «своего значения» карточек; жёсткий кламп по границам).
 *
 * Пустое значение допустимо (null) — «не задано»: у карточек это значит
 * «вставка в промпт не делается». Кнопки от пустого стартуют с min (или 1).
 * Ввод — только цифры (inputmode numeric, мобильная клавиатура цифровая),
 * кламп по min/max на blur и на кнопках.
 *
 * Реюз: «Свой вариант» числового типа (GenerateToolMode) и поле значения
 * мультистиль-карточек.
 */
import { useState } from 'react'

interface Props {
	value: number | null
	onChange: (v: number | null) => void
	min?: number
	max?: number
	disabled?: boolean
	ariaLabel?: string
}

export default function NumberStepper({ value, onChange, min, max, disabled, ariaLabel }: Props) {
	// Черновик строки: даёт стирать до пустого и печатать без промежуточных
	// клампов; коммит с клампом — на blur.
	const [draft, setDraft] = useState<string | null>(null)

	const clamp = (n: number): number => {
		if (min != null && n < min) return min
		if (max != null && n > max) return max
		return n
	}

	const commit = (raw: string) => {
		setDraft(null)
		const digits = raw.replace(/\D/g, '')
		if (!digits) {
			onChange(null)
			return
		}
		onChange(clamp(parseInt(digits, 10)))
	}

	const step = (delta: number) => {
		const base = value ?? (min ?? 1) - delta
		onChange(clamp(base + delta))
	}

	const shown = draft ?? (value == null ? '' : String(value))
	// Пустое поле показывает минимум ПЛЕЙСХОЛДЕРОМ (владелец 2026-08-16:
	// «от» из админки — ориентир, а не значение; в промпт не подставляется).
	const placeholder = min != null ? String(min) : '—'

	return (
		<span className={'num-stepper' + (disabled ? ' num-stepper--disabled' : '')}>
			<button
				type="button"
				className="num-stepper__btn"
				onClick={() => step(-1)}
				disabled={disabled || (min != null && (value ?? min) <= min)}
				aria-label="Меньше"
			>
				−
			</button>
			<input
				className="num-stepper__input"
				type="text"
				inputMode="numeric"
				pattern="[0-9]*"
				value={shown}
				placeholder={placeholder}
				onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
				onBlur={(e) => commit(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
				}}
				disabled={disabled}
				aria-label={ariaLabel || 'Число'}
			/>
			<button
				type="button"
				className="num-stepper__btn"
				onClick={() => step(1)}
				disabled={disabled || (max != null && (value ?? 0) >= max)}
				aria-label="Больше"
			>
				+
			</button>
		</span>
	)
}
